import { useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';

// Promoted to the cross-service shared components; re-exported so existing
// compute imports keep working.
export { RowMenu } from '../../components/RowMenu';

export interface CreateField {
  key: string;
  label: string;
  placeholder?: string;
  /** Render as a dropdown instead of a free-text input. */
  type?: 'text' | 'select';
  options?: { value: string; label: string }[];
  /** Exclude from the required-to-submit check. */
  optional?: boolean;
  /** Initial value (e.g. a default select option). */
  default?: string;
}

/** Field control shared by the create/edit dialogs: a labelled text input or
 *  dropdown driven by a CreateField descriptor. */
export function CreateFieldControl({ field, value, onChange }: { field: CreateField; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={field.label} required={!field.optional}>
      {(p) =>
        field.type === 'select' ? (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger {...p} style={{ width: '100%' }}>
              <SelectValue placeholder={field.placeholder ?? 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options ?? []).map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input {...p} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
        )
      }
    </Field>
  );
}

/** True when every required (non-optional) field has a value. */
export function fieldsValid(fields: CreateField[], values: Record<string, string>): boolean {
  return fields.every((f) => f.optional || (values[f.key] ?? '').trim());
}

/** Minimal create dialog: a list of fields → payload object. */
export function SimpleCreateDialog({
  open,
  onOpenChange,
  title,
  fields,
  submitLabel = 'Create',
  onCreate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  fields: CreateField[];
  submitLabel?: string;
  onCreate: { mutate: (payload: Record<string, unknown>) => void; isPending: boolean };
}) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.filter((f) => f.default != null).map((f) => [f.key, f.default as string])),
  );
  const valid = fieldsValid(fields, values);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {fields.map((f) => (
            <CreateFieldControl
              key={f.key}
              field={f}
              value={values[f.key] ?? ''}
              onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
            />
          ))}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!valid || onCreate.isPending} onClick={() => onCreate.mutate(values)}>
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
